import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import copy from 'copy-to-clipboard';
import Axios from 'axios';

import {
  Box, Checkbox, Drawer, FormControl, IconButton, MenuItem, Grid,
  OutlinedInput, Select, SelectChangeEvent, Typography, CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  FileCopyOutlined as CopyIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';

import { useAuth } from 'src/hooks/useAuth';
import ConfigContext from 'src/contexts/ConfigContext';
import SubmitBtn from 'src/shared/buttons/SubmitBtn';
import CancelBtn from 'src/shared/buttons/CancelBtn';
import styles from './style';

interface PropTypes {
  open: boolean;
  onClose: () => void;
  nodeType: string | null;
  nodeId: string | null;
  label: string | null;
  configuration: Record<string, any>;
  isSaving?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfigurationSave: (nodeId: string, configuration: Record<string, any>) => void;
}

const DocumentInputComponent = (props: PropTypes) => {
  const { ready, t } = useTranslation();
  const { user } = useAuth();
  const { API } = useContext(ConfigContext);

  const { open, onClose, nodeId, configuration, isSaving = false, onConfigurationSave } = props;

  const [loading, setLoading] = useState(false);

  const [inputFormats] = useState(['pdf', 'jpeg', 'jpg', 'png', 'tiff', 'heic']);
  const [inputSources] = useState([{
    value: 'app', label: 'WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_APP'
  }, {
    value: 'email', label: 'WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_EMAIL'
  }, {
    value: 'api', label: 'WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_API'
  }]);
  const [selectedInputFormats, setSelectedInputFormats] = useState<string[]>([]);
  const [selectedInputSources, setSelectedInputSources] = useState<string[]>([]);

  const [apiAccessToken, setAPIAccessToken] = useState('');
  const [apiRefreshToken, setAPIRefreshToken] = useState('');
  const [apiKeyToken, setAPIKeyToken] = useState('');
  const [apiKeyRefreshToken, setAPIKeyRefreshToken] = useState('');

  const [copyObj, setCopyObj] = useState<Record<string, boolean>>({
    email: false,
    companyID: false,
    apiAccessToken: false,
    apiRefreshToken: false,
    apiKeyToken: false,
    apiKeyRefreshToken: false,
  });

  const onChangeCopy = (prop: string) => {
    const iCopyObj: Record<string, boolean> = {
      email: false,
      companyID: false,
      apiAccessToken: false,
      apiRefreshToken: false,
      apiKeyToken: false,
      apiKeyRefreshToken: false,
    };

    iCopyObj[prop] = true;

    setCopyObj(iCopyObj);
  };

  const copyToClipboard = (prop: string, value: string) => {
    copy(value, {
      // @ts-ignore
      onCopy: onChangeCopy(prop)
    });
  };

  const handleMultiSelectChange = (event: SelectChangeEvent<typeof selectedInputFormats>) => {
    const {
      target: { value },
    } = event;
    setSelectedInputFormats(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleChangeInputSource = (event: SelectChangeEvent<typeof selectedInputSources>) => {
    const {
      target: { value },
    } = event;
    setSelectedInputSources(
      typeof value === 'string' ? value.split(',') : value,
    );
    setCopyObj({
      email: false,
      companyID: false,
      apiAccessToken: false,
      apiRefreshToken: false,
      apiKeyToken: false,
      apiKeyRefreshToken: false,
    });
  };

  useEffect(() => {
    getTokenPairFromAccessToken();
  }, []);

  useEffect(() => {
    if (configuration) {
      setSelectedInputFormats(configuration.fileFormats || []);
      setSelectedInputSources(configuration.source || []);
    }
  }, [configuration]);

  const getTokenPairFromAccessToken = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(
        `${API.getTokenPairFromAccessToken}/${localStorage.getItem('PROCYS_accessToken')}`
      );
      if (response.data.success) {
        setAPIAccessToken(response.data.data.accessToken);
        setAPIRefreshToken(response.data.data.refreshToken);
        setAPIKeyToken(response.data.data.apiToken);
        setAPIKeyRefreshToken(response.data.data.apiRefreshToken);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (nodeId) {
      const configuration = {
        source: selectedInputSources,
        fileFormats: selectedInputFormats,
      };
      onConfigurationSave(nodeId, configuration);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <Box sx={styles.container}>
        <Box sx={styles.modalHeader}>
          <Box sx={styles.titleContainer}>
            <Typography sx={styles.headerTitle}>
              {t('WORKFLOW_COMPONENT_DOC_INPUT')}
            </Typography>
            <IconButton sx={styles.closeBtn} onClick={onClose}>
              <CloseIcon sx={styles.closeIcon} />
            </IconButton>
          </Box>
        </Box>
        <Grid sx={styles.modalContent} className="y-scroll">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={styles.addNewForm}>
              <Box sx={styles.fieldContainer}>
                <Typography sx={styles.descr}>{t('WORKFLOW_COMPONENT_DOC_INPUT_DESCRIPTION')}</Typography>
              </Box>
              <Box sx={styles.fieldContainer}>
                <Typography sx={styles.label}>{t('WORKFLOW_COMPONENT_DOC_INPUT_FORMATS')}</Typography>
                <FormControl fullWidth sx={styles.formControl}>
                  <Select
                    id="input-formats"
                    multiple
                    value={selectedInputFormats}
                    onChange={handleMultiSelectChange}
                    input={<OutlinedInput />}
                    renderValue={(selected) => selected.join(', ')}
                    sx={styles.selectStyle}
                  >
                    {inputFormats.map((format) => (
                      <MenuItem sx={styles.selectItem} key={format} value={format}>
                        <Checkbox sx={styles.checkBox} checked={selectedInputFormats.includes(format)} />
                        {format}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={styles.fieldContainer}>
                <Typography sx={styles.label}>{t('WORKFLOW_COMPONENT_DOC_INPUT_SOURCE')}</Typography>
                <FormControl fullWidth sx={styles.formControl}>
                  <Select
                    id="input-sources"
                    multiple
                    value={selectedInputSources}
                    onChange={handleChangeInputSource}
                    input={<OutlinedInput />}
                    renderValue={(selected) => selected.join(', ')}
                    sx={styles.selectStyle}
                  >
                    {inputSources.map((source) => (
                      <MenuItem sx={styles.selectItem} key={source.value} value={source.value}>
                        <Checkbox sx={styles.checkBox} checked={selectedInputSources.includes(source.value)} />
                        {t(source.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {selectedInputSources.includes('email') && (
                <Box sx={styles.fieldContainer}>
                  <Typography sx={styles.label}>{`${t('WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_EMAIL')}:`}</Typography>
                  <Typography sx={styles.label}>{t('INTEGRATIONS_EMAIL_FORWARDING_EMAIL')}</Typography>
                  <Box sx={styles.detailRow}>
                    <input
                      type="text"
                      value={user?.ocrEmail}
                      style={styles.inputField}
                      disabled
                    />
                    <IconButton sx={styles.copyBtn} onClick={() => copyToClipboard('email', user?.ocrEmail || '')}>
                      <CopyIcon sx={styles.copyIcon} />
                    </IconButton>
                    {copyObj.email && <CheckIcon style={styles.checkIcon} />}
                  </Box>
                </Box>
              )}
              {selectedInputSources.includes('api') && (
                <>
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>{`${t('WORKFLOW_COMPONENT_DOC_INPUT_SOURCE_API')}:`}</Typography>
                    <Typography sx={styles.label}>{t('TOPBAR_USERMENU_ID')}</Typography>
                    <Box sx={styles.detailRow}>
                      <input
                        type="text"
                        value={user?.companyID}
                        style={styles.inputField}
                        disabled
                      />
                      <IconButton sx={styles.copyBtn} onClick={() => copyToClipboard('companyID', user?.companyID || '')}>
                        <CopyIcon sx={styles.copyIcon} />
                      </IconButton>
                      {copyObj.companyID && <CheckIcon style={styles.checkIcon} />}
                    </Box>
                  </Box>
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>{t('INTEGRATIONS_API_ACCESS_TOKEN')}</Typography>
                    <Box sx={styles.detailRow}>
                      <input
                        type="text"
                        value={apiAccessToken}
                        style={styles.inputField}
                        disabled
                      />
                      <IconButton sx={styles.copyBtn} onClick={() => copyToClipboard('apiAccessToken', apiAccessToken || '')}>
                        <CopyIcon sx={styles.copyIcon} />
                      </IconButton>
                      {copyObj.apiAccessToken && <CheckIcon style={styles.checkIcon} />}
                    </Box>
                  </Box>
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>{t('INTEGRATIONS_API_REFRESH_TOKEN')}</Typography>
                    <Box sx={styles.detailRow}>
                      <input
                        type="text"
                        value={apiRefreshToken}
                        style={styles.inputField}
                        disabled
                      />
                      <IconButton sx={styles.copyBtn} onClick={() => copyToClipboard('apiRefreshToken', apiRefreshToken || '')}>
                        <CopyIcon sx={styles.copyIcon} />
                      </IconButton>
                      {copyObj.apiRefreshToken && <CheckIcon style={styles.checkIcon} />}
                    </Box>
                  </Box>
                  <Box sx={styles.integrationBody}>
                    <Typography sx={styles.integrationTitle}>
                      {ready && t('INTEGRATIONS_SECONDARY_API_KEY')}
                    </Typography>
                    <Typography sx={styles.integrationDesc}>
                      {ready && t('INTEGRATIONS_SECONDARY_API_KEY_DESCRIPTION')}
                    </Typography>
                  </Box>
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>{t('INTEGRATIONS_API_ACCESS_TOKEN')}</Typography>
                    <Box sx={styles.detailRow}>
                      <input
                        type="text"
                        value={apiKeyToken}
                        style={styles.inputField}
                        disabled
                      />
                      <IconButton sx={styles.copyBtn} onClick={() => copyToClipboard('apiKeyToken', apiKeyToken || '')}>
                        <CopyIcon sx={styles.copyIcon} />
                      </IconButton>
                      {copyObj.apiKeyToken && <CheckIcon style={styles.checkIcon} />}
                    </Box>
                  </Box>
                  <Box sx={styles.fieldContainer}>
                    <Typography sx={styles.label}>{t('INTEGRATIONS_API_REFRESH_TOKEN')}</Typography>
                    <Box sx={styles.detailRow}>
                      <input
                        type="text"
                        value={apiKeyRefreshToken}
                        style={styles.inputField}
                        disabled
                      />
                      <IconButton sx={styles.copyBtn} onClick={() => copyToClipboard('apiKeyRefreshToken', apiKeyRefreshToken || '')}>
                        <CopyIcon sx={styles.copyIcon} />
                      </IconButton>
                      {copyObj.apiKeyRefreshToken && <CheckIcon style={styles.checkIcon} />}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Grid>
        <Box sx={styles.modalFooter}>
          <Box display="flex" justifyContent="flex-end" gap="16px">
            <CancelBtn
              title={t('WORKFLOW_BUTTON_CANCEL')}
              onClick={onClose}
            />
            <SubmitBtn
              title={t('WORKFLOW_BUTTON_SAVE')}
              disabled={loading || isSaving || selectedInputFormats.length === 0 || selectedInputSources.length === 0}
              onClick={handleSave}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

export default DocumentInputComponent;
